import React, { useState, useRef, useEffect, useCallback } from 'react'; // Added useCallback
import { NavLink } from 'react-router-dom';

import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import CodeIcon from '@mui/icons-material/Code';
import LinkIcon from '@mui/icons-material/Link';
import MailIcon from '@mui/icons-material/Mail';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import HandymanIcon from '@mui/icons-material/Handyman';
import SlideshowIcon from '@mui/icons-material/Slideshow';

const SideBar = () => {
  const [openCategory, setOpenCategory] = useState(null);
  const [allOpen, setAllOpen] = useState(true);
  const [collapsed, setCollapsed] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0); // State for unread messages
  const [dailyImageUploadCount, setDailyImageUploadCount] = useState(0); // NEW: State for daily image uploads
  const sidebarRef = useRef();

  const API_BASE_URL =
    process.env.NODE_ENV === 'production'
      ? process.env.REACT_APP_API_URL_PRODUCTION
      : process.env.REACT_APP_API_URL;

  // Function to fetch unread message count
  const fetchUnreadCount = useCallback(async () => { // Wrapped in useCallback
    try {
      const res = await fetch(`${API_BASE_URL}/api/messages/unread-count`);
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count);
      } else {
        console.error('Failed to fetch unread message count');
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error fetching unread message count:', error);
      setUnreadCount(0);
    }
  }, [API_BASE_URL]); // Dependency for useCallback

  // NEW: Function to fetch daily image upload count
  const fetchDailyImageUploadCount = useCallback(async () => { // Wrapped in useCallback
    try {
      const res = await fetch(`${API_BASE_URL}/api/images/daily-upload-count`);
      if (res.ok) {
        const data = await res.json();
        setDailyImageUploadCount(data.count);
      } else {
        console.error('Failed to fetch daily image upload count');
        setDailyImageUploadCount(0);
      }
    } catch (error) {
      console.error('Error fetching daily image upload count:', error);
      setDailyImageUploadCount(0);
    }
  }, [API_BASE_URL]); // Dependency for useCallback

  // Listen for custom 'messageRead' event (renamed to reflect broader count changes) and periodic fetches
  useEffect(() => {
    fetchUnreadCount(); // Initial fetch for messages
    fetchDailyImageUploadCount(); // Initial fetch for images

    const messageInterval = setInterval(fetchUnreadCount, 60000); // Periodic fetch for messages
    const imageInterval = setInterval(fetchDailyImageUploadCount, 60000); // Periodic fetch for images

    // This handler will be triggered by both 'messageRead' from Inbox and a new 'imageUpdated' from Images
    const handleCountUpdate = () => {
      fetchUnreadCount(); // Re-fetch immediately when messageRead event is dispatched
      fetchDailyImageUploadCount(); // Re-fetch immediately when imageUpdated event is dispatched (or messageRead)
    };

    window.addEventListener('messageRead', handleCountUpdate); // Reusing event name for generic count updates
    window.addEventListener('imageUpdated', handleCountUpdate); // NEW: Listen for image updates

    return () => {
      clearInterval(messageInterval); // Clean up periodic message interval
      clearInterval(imageInterval); // Clean up periodic image interval
      window.removeEventListener('messageRead', handleCountUpdate); // Clean up message event listener
      window.removeEventListener('imageUpdated', handleCountUpdate); // Clean up image event listener
    };
  }, [fetchUnreadCount, fetchDailyImageUploadCount]); // Dependencies for useEffect

  // Collapse when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setCollapsed(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleCategory = (category) => {
    if (allOpen) setAllOpen(false);
    setOpenCategory(openCategory === category ? null : category);
  };

  const toggleAll = () => {
    if (allOpen) {
      setOpenCategory(null);
      setAllOpen(false);
    } else {
      setAllOpen(true);
      setOpenCategory(null);
    }
  };

  const isOpen = (category) => allOpen || openCategory === category;

  const handleLinkClick = () => {
    setCollapsed(true);
  };

  // Modified SidebarLink component to accept and display a badge
  const SidebarLink = ({ to, icon: Icon, label, badgeCount }) => (
    <NavLink to={to} className="sideBarLinkDiv" onClick={handleLinkClick}>
      <div className="sideBarLinkIconDiv">
        <Icon className="sideBarLinkIcon" />
      </div>
      <div className="sideBarLink">{label}</div>
      {badgeCount > 0 && ( // Conditionally render badge if count is greater than 0
        <span className="unread-badge">{badgeCount}</span>
      )}
    </NavLink>
  );

  return (
    <div className={`left-sidebar ${collapsed ? 'collapsed' : ''}`} ref={sidebarRef}>
      <div className="sideBarDiv sideBar">
        {/* Toggle All */}
        <div
          className="sideBarViewButton viewAll"
          onClick={toggleAll}
          title={allOpen ? "Collapse all" : "Expand all"}
        >
          {allOpen ? <VisibilityOffIcon /> : <VisibilityIcon />}
          <span style={{ marginLeft: 8 }}>{allOpen ? "Hide All" : "View All"}</span>
        </div>

        {/* Static Links */}
        <SidebarLink to="/dashboard" icon={DashboardIcon} label="Dashboard" />
        <SidebarLink to="/inbox" icon={MailIcon} label="Inbox" badgeCount={unreadCount} />
        <SidebarLink to="/sandbox" icon={HandymanIcon} label="Sandbox" />

        {/* Websites Dropdown */}
        <div className="sideBarCategory">
          <div className="sideBarCategoryHeader" onClick={() => toggleCategory('websites')}>
            <span>Websites</span>
            {isOpen('websites') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
          
          {isOpen('websites') && (
            <div className="sideBarCategoryLinks">
              <SidebarLink to="/webstore-admin" icon={SlideshowIcon} label="Webstore" />
            </div>

          )}
        </div>
 
        {/* Tech Dropdown */}
        <div className="sideBarCategory">
          <div className="sideBarCategoryHeader" onClick={() => toggleCategory('projects')}>
            <span>Tech</span>
            {isOpen('projects') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
          {isOpen('projects') && (
            <div className="sideBarCategoryLinks">
              <SidebarLink to="/projects" icon={FolderCopyIcon} label="Projects" />
              <SidebarLink to="/links" icon={LinkIcon} label="Links" />
              <SidebarLink to="/codes" icon={CodeIcon} label="Codes" />
              {/* NEW: Pass dailyImageUploadCount to the Images link */}
              <SidebarLink to="/images" icon={CodeIcon} label="Images" badgeCount={dailyImageUploadCount} />
              <SidebarLink to="/videos" icon={CodeIcon} label="Videos"/>
            </div>
          )}
        </div>

        

        {/* Guides Dropdown */}
        {/* <div className="sideBarCategory">
          <div className="sideBarCategoryHeader" onClick={() => toggleCategory('guides')}>
            <span>Guides</span>
            {isOpen('guides') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
          {isOpen('guides') && (
            <div className="sideBarCategoryLinks">
              <SidebarLink to="/guides" icon={EmojiObjectsIcon} label="Guides" />
            </div>
          )}
        </div> */}

        {/* Jalen Dropdown */}
        <div className="sideBarCategory">
          <div className="sideBarCategoryHeader" onClick={() => toggleCategory('notes')}>
            <span>Jalen</span>
            {isOpen('notes') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
          {isOpen('notes') && (
            <div className="sideBarCategoryLinks">
              <SidebarLink to="/notes" icon={TextSnippetIcon} label="Notes" />
              <SidebarLink to="/dates" icon={EditCalendarIcon} label="Dates" />              
              <SidebarLink to="/presentations" icon={SlideshowIcon} label="Presentation" />
            </div>
          )}
        </div>

        {/* Settings */}
        <div className="sideBarCategory">
          <div className="sideBarCategoryHeader" onClick={() => toggleCategory('settings')}>
            <span>Settings</span>
            {isOpen('settings') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
          {isOpen('settings') && (
            <div className="sideBarCategoryLinks">
              <SidebarLink to="/resume/admin" icon={SlideshowIcon} label="Resume" />
            </div>
          )}
        </div>
      </div>

      {/* Toggle Sidebar Collapse */}
      <div
        className="sidebarToggleBtn"
        onClick={() => setCollapsed(!collapsed)}
      >
        {collapsed ? <ArrowForwardIosIcon /> : <ArrowBackIosIcon />}
      </div>
    </div>
  );
};

export default SideBar;